import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnInit } from "@angular/core";
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { isEmpty } from "lodash";
import moment from "moment";
import { distinctUntilChanged, skip } from "rxjs/operators";

import { Subscription } from "rxjs";
import { CustomValidationService } from "src/app/cbta-web/services/custom-validation.service";
import { CONSTANT } from "src/app/constant";
import { StaffDetails } from "src/app/models/models";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { ProfileService } from "src/app/shared/services/profile.service";
import { SharedService } from "src/app/shared/services/shared.service";
import {
	clearStaffLookupFailureOnValueChange,
	patchStaffLicenseExpiryDate,
	rejectInstructorOwnStaffNumber,
} from "src/app/shared/services/staff-lookup.helper";
import { ContainerRatingsService } from "../../services/base/container-ratings.services";

type SessionItem = { code: string; description: string };
type SessionGroupVM = { code: string; key: string; items: SessionItem[] };
type ExemptHit = {
	groupCode: string; // e.g. "trainingSession"
	groupKey: string; // display name (from VM)
	itemCode: string; // e.g. "RHS"
	description?: string; // e.g. "Right Hand Seat"
	normalizedCode: string; // uppercased, slash/underscore preserved
	controlPath: string; // e.g. "sessionDetails.trainingSession.RHS"
	currentValue: any; // the actual boolean in the control
};

@Component({
	selector: "app-candidate-details-group-flight",
	templateUrl: "./candidate-details-group-flight.component.html",
	styleUrls: ["./candidate-details-group-flight.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CandidateDetailsGroupFlightComponent implements OnInit {
	// ------ Form wiring ------
	candidateForm!: UntypedFormGroup; // the candidate row form (passed in)
	parentForm!: UntypedFormArray; // parent array containing all candidates
	parentFormArrayIndex = 0;
	formType!: string | undefined;
	isTS1: boolean = false;
	isTS3: boolean = false;
	isTS5: boolean = false;

	// ------ Display / UX ------
	panelOpenState = false;
	isOnline = true;
	isBootstrapping = true; // prevents firing autosaves while we wire controls

	// flight/sim section disabled flag (used to hide “×” and look fresh)
	flightSimDisabled = false;

	// ------ Dates ------
	DATE_FORMAT = CONSTANT.DATE_FORMAT;
	today: Date = new Date();
	currentDate: Date = new Date();
	datePickerRef?: ElementRef;
	datePickerInput = new UntypedFormControl(new Date(), { updateOn: "blur" });

	// ------ Sessions data prepared for the template ------
	sessions!: any; // raw sessions input from parent
	sessionGroups: SessionGroupVM[] = []; // normalized groups for UI
	singleCols: SessionItem[][] = []; // single-group layout split into 4 cols
	twoColsLeft?: SessionGroupVM; // 2-group layout left column
	twoColsRight: SessionItem[][] = []; // 2-group layout right (split into 2 cols if needed)
	secondGroupTitle = "";
	twoGroupsCompact = false; // right side <= 4 items

	lastColTitle = ""; // 2-group layout: right title           // 2-group layout: right has <= 4 items
	lastCols?: SessionItem[][] = [];

	private notReqSub?: Subscription;
	private otherSessionSubs: Subscription[] = [];

	// Sessions that disable Flight/Simulator fields
	private readonly EXEMPT_SESSIONS = new Set([
		"LFUS",
		"LPC/OPC",
		"LPC_OPC",
		"LPC",
		"OPC",
		"LOFT",
		"FFS",
		"RHS",
		"UPRT",
	]);

	// Sessions that disable Flight/Simulator fields for ETS-3 and ETS-5
	// Note: ICRM/RCRM and DGI/DGR are combined values, not separate
	private readonly TS3_TS5_EXEMPT_SESSIONS = new Set([
		"XFCC",
		"REC",
		"ICRM_RCRM", // Combined value only
		"DGI_DGR", // Combined value only
	]);

	constructor(
		public sharedService: SharedService,
		private httpService: HttpService,
		private loaderService: LoaderService,
		private cvs: CustomValidationService,
		private cd: ChangeDetectorRef,
		public containerRatingService: ContainerRatingsService,
		private profileService: ProfileService,
	) {}

	// ========== Inputs from parent ==========
	/** The whole candidate row form. */
	@Input("candidateForm")
	set _(value: any) {
		this.formType = this.containerRatingService.formType;

		if (this.formType == CONSTANT.FORM_TYPE.ts_1) {
			this.isTS1 = true;
		} else if (this.formType == CONSTANT.FORM_TYPE.ts_3) {
			this.isTS3 = true;
		} else if (this.formType == CONSTANT.FORM_TYPE.ts_5) {
			this.isTS5 = true;
		}

		this.candidateForm = value as UntypedFormGroup;
		this.initFlightDetails();
		this.ensureSessionFormStructure();
		this.ensureFlightSimFormStructure();

		this.attachFlightSimToggleWatcher();

		// this.maybeApplyInitialDisablement();
	}

	/** Raw sessions config (group + details) to render the checkboxes. */
	@Input("sessions")
	set sessionsInput(value: any) {
		this.sessions = value;
		this.rebuildSessionLayoutFromInput();
		this.ensureSessionFormStructure();
		this.wireUpNotRequiredMutualExclusion();
		this.cd.detectChanges();
	}

	initFlightDetails() {
		const sd = this.candidateForm?.value?.sessionDetails ?? {};
		// collect ALL keys across all groups where the value is true
		const selected: string[] = Object.values(sd).flatMap((group: any) =>
			Object.entries(group ?? {})
				.filter(([, v]) => v === true)
				.map(([k]) => k),
		);

		let hasExempt = false;

		// For TS3 and TS5, check for TS3/TS5 specific exempt sessions
		if (this.isTS3 || this.isTS5) {
			hasExempt = selected.some((code) => {
				const normalizedCode = (code ?? "").toString().toUpperCase().trim();
				// Check both the original format and normalized format (slash/underscore variants)
				return (
					this.TS3_TS5_EXEMPT_SESSIONS.has(normalizedCode) ||
					this.TS3_TS5_EXEMPT_SESSIONS.has(normalizedCode.replace(/\//g, "_")) ||
					this.TS3_TS5_EXEMPT_SESSIONS.has(normalizedCode.replace(/_/g, "/"))
				);
			});
		} else {
			// For other forms (not TS1, TS3, TS5), check for general exempt sessions
			hasExempt =
				!this.isTS1 && selected.some((code) => this.EXEMPT_SESSIONS.has((code ?? "").toString().toUpperCase()));
		}

		this.applyFlightSimDisablement(hasExempt);
	}

	// ========== Lifecycle ==========
	ngOnInit(): void {
		this.isOnline = navigator.onLine;

		// online/offline status (if provided)
		this.sharedService.getIsStatusOnline().subscribe((status: boolean) => {
			this.isOnline = status;
		});

		// find index in parent array
		this.parentForm = this.candidateForm.parent as UntypedFormArray;
		this.parentFormArrayIndex =
			this.parentForm?.controls?.findIndex?.((subform) => subform === this.candidateForm) ?? 0;

		this.staffNumber.valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
			clearStaffLookupFailureOnValueChange(this.staffNumber);
			this.rejectOwnStaffNumber();
		});
		this.rejectOwnStaffNumber();

		// keep datepicker input in sync with licenseExpiryDate (UI only)
		this.licenseExpiryDate.valueChanges.pipe(distinctUntilChanged(), skip(1)).subscribe((val) => {
			if (this.isBootstrapping || !val) return;
			const date = moment(val).toDate();
			this.datePickerInput.patchValue(date, { emitEvent: false });
			this.updateDisplayDateFormat(date);
		});

		// all set
		this.candidateForm.markAsPristine();
		this.isBootstrapping = false;
		// this.maybeApplyInitialDisablement();
		// this.applyFlightSimDisablement(this.isAnyExemptSessionSelected());
	}

	private getExemptHits(): ExemptHit[] {
		const sd = this.sessionsDetails;
		if (!sd) return [];

		const hits: ExemptHit[] = [];

		for (const g of this.sessionGroups) {
			const grp = sd.get(g.code) as UntypedFormGroup | null;
			if (!grp) continue;

			for (const item of g.items) {
				const ctrl = grp.get(item.code) as UntypedFormControl | null;
				if (!ctrl) continue;

				const normalized = (item.code ?? "").toString().trim().toUpperCase();
				if (ctrl.value === true && this.EXEMPT_SESSIONS.has(normalized)) {
					hits.push({
						groupCode: g.code,
						groupKey: g.key,
						itemCode: item.code,
						description: item.description,
						normalizedCode: normalized,
						controlPath: `sessionDetails.${g.code}.${item.code}`,
						currentValue: ctrl.value,
					});
				}
			}
		}

		return hits;
	}

	ngOnDestroy(): void {
		this.teardownSessionSubscriptions();
	}

	// ========== Small layout helpers ==========
	private splitIntoColumns<T>(items: T[], columns: number): T[][] {
		if (columns <= 0) return [];
		if (columns === 1) return [items.slice()];

		if (columns === 2) {
			const n = items.length;
			// Even-ish split, but first column must have at least 5 (if available)
			const firstCount = Math.min(n, Math.max(5, Math.ceil(n / 2)));
			return [items.slice(0, firstCount), items.slice(firstCount)];
		}

		// Fallback for 3+ columns (simple round-robin or keep your previous logic)
		const cols: T[][] = Array.from({ length: columns }, () => []);
		items.forEach((item, idx) => cols[idx % columns].push(item));
		return cols;
	}

	private teardownSessionSubscriptions() {
		if (this.notReqSub) {
			this.notReqSub.unsubscribe();
			this.notReqSub = undefined;
		}
		this.otherSessionSubs.forEach((s) => s.unsubscribe());
		this.otherSessionSubs = [];
	}

	private findNotRequiredControl(): UntypedFormControl | null {
		const sd = this.sessionsDetails;
		if (!sd) return null;
		for (const g of this.sessionGroups) {
			const grp = sd.get(g.code) as UntypedFormGroup | null;
			if (!grp) continue;
			const c = grp.get("not_required") as UntypedFormControl | null;
			if (c) return c;
		}
		return null;
	}

	private wireUpNotRequiredMutualExclusion() {
		this.teardownSessionSubscriptions();
		const sd = this.sessionsDetails;
		if (!sd) return;

		const notReq = this.findNotRequiredControl();
		if (!notReq) return;

		// If not_required → true, uncheck everything else (in all groups)
		this.notReqSub = notReq.valueChanges.subscribe((val: boolean) => {
			if (val !== true) return;
			for (const g of this.sessionGroups) {
				const grp = sd.get(g.code) as UntypedFormGroup | null;
				if (!grp) continue;
				Object.keys(grp.controls).forEach((k) => {
					if (k === "not_required") return;
					const c = grp.get(k) as UntypedFormControl;
					if (c?.value === true) c.setValue(false, { emitEvent: false });
				});
			}
			// fire once after batch changes
			sd.updateValueAndValidity({ emitEvent: true });
		});

		// If any other session → true, force not_required → false
		for (const g of this.sessionGroups) {
			const grp = sd.get(g.code) as UntypedFormGroup | null;
			if (!grp) continue;
			Object.keys(grp.controls).forEach((k) => {
				if (k === "not_required") return;
				const c = grp.get(k) as UntypedFormControl;
				const sub = c.valueChanges.subscribe((val: boolean) => {
					if (val === true && notReq.value === true) {
						notReq.setValue(false, { emitEvent: false });
						sd.updateValueAndValidity({ emitEvent: true });
					}
				});
				this.otherSessionSubs.push(sub);
			});
		}
	}

	/** Convert raw `sessions` input to view-model groups and compute columns. */
	private rebuildSessionLayoutFromInput(): void {
		// Push "others" group to the end (unchanged behavior you wanted earlier)
		const isOthers = (s: any) => s.name?.code?.toLowerCase() === "other";

		const sessionsRaw = this.sessions || [];
		const sessions = [...sessionsRaw.filter((s: any) => !isOthers(s)), ...sessionsRaw.filter(isOthers)];

		const groups: SessionGroupVM[] = (sessions ?? []).map((g: any) => ({
			code: g?.name?.code ?? "group",
			key: g?.name?.description ?? "Group",
			items: (g?.details ?? []).map((d: any) => ({
				code: d?.code,
				description: d?.description,
			})),
		}));

		this.sessionGroups = groups;

		if (this.sessionGroups.length === 1) {
			this.singleCols = this.splitIntoColumns(this.sessionGroups[0].items, 4);
			this.twoColsLeft = undefined;
			this.twoColsRight = [];
			this.twoGroupsCompact = false;
			this.secondGroupTitle = "";
		} else if (this.sessionGroups.length === 4) {
			this.twoColsLeft = this.sessionGroups[0];
			const rightItems = this.sessionGroups[1].items;
			const lastItems = this.sessionGroups[2].items;
			this.twoGroupsCompact = rightItems.length <= 4;
			this.twoColsRight = this.twoGroupsCompact ? [] : this.splitIntoColumns(rightItems, 2);
			this.singleCols = [];
			this.secondGroupTitle = this.sessionGroups[1].key;
			this.lastCols = this.splitIntoColumns(lastItems, 2);
			this.lastColTitle = this.sessionGroups[2].key;
		} else if (this.sessionGroups.length == 3) {
			this.twoColsLeft = this.sessionGroups[0];
			const rightItems = this.sessionGroups[1].items;
			const lastItems = this.sessionGroups[2].items;
			this.twoGroupsCompact = rightItems.length <= 4;
			this.twoColsRight = this.twoGroupsCompact ? [] : this.splitIntoColumns(rightItems, 2);
			this.singleCols = [];
			this.secondGroupTitle = this.sessionGroups[1].key;
			this.lastCols = this.splitIntoColumns(lastItems, 2);
			this.lastColTitle = this.sessionGroups[2].key;
		} else {
			this.singleCols = [];
			this.twoColsLeft = undefined;
			this.twoColsRight = [];
			this.twoGroupsCompact = false;
			this.secondGroupTitle = "";
		}

		this.wireUpNotRequiredMutualExclusion();
	}

	/**
	 * Ensure the nested FormGroup structure exists for Sessions:
	 * candidateForm.sessionDetails.<groupCode>.<detailCode>: boolean
	 */
	private ensureSessionFormStructure(): void {
		if (!this.candidateForm) return;

		const sd = this.sessionsDetails;
		if (!sd) return;

		const variants = (code: string) => {
			const c = (code ?? "").trim();
			const set = new Set([c, c.replace(/\//g, "_"), c.replace(/_/g, "/")]);
			return Array.from(set);
		};

		for (const g of this.sessionGroups) {
			if (!sd.get(g.code)) {
				sd.addControl(g.code, new UntypedFormGroup({}), { emitEvent: false });
			}
			const grp = sd.get(g.code) as UntypedFormGroup;

			for (const item of g.items) {
				const key = item.code;
				const alts = variants(key);

				// If the exact key doesn't exist, look for an alias (e.g. LPC_OPC vs LPC/OPC)
				if (!grp.get(key)) {
					const aliasKey = alts.find((k) => !!grp.get(k));
					if (aliasKey) {
						const val = (grp.get(aliasKey) as UntypedFormControl).value;
						grp.removeControl(aliasKey, { emitEvent: false });
						grp.addControl(key, new UntypedFormControl(val), { emitEvent: false });
					} else {
						grp.addControl(key, new UntypedFormControl(false), { emitEvent: false });
					}
				}
			}

			// Remove any stale/alias controls that aren't in the VM list
			const validKeys = new Set(g.items.map((i) => i.code));
			Object.keys(grp.controls).forEach((k) => {
				if (!validKeys.has(k)) grp.removeControl(k, { emitEvent: false });
			});
		}

		sd.updateValueAndValidity({ emitEvent: false });
		this.candidateForm.updateValueAndValidity({ emitEvent: false });
	}

	/**
	 * Ensure the nested FormGroup structure exists for Flight/Simulator details:
	 * candidateForm.flightSimulatorDetails.{aircraftSimReg, acType, airportRoute, sectorNo, accumulatedSectors}
	 */
	private ensureFlightSimFormStructure(): void {
		if (!this.candidateForm) return;

		if (!this.candidateForm.get("flightSimulatorDetails")) {
			this.candidateForm.addControl("flightSimulatorDetails", new UntypedFormGroup({}), { emitEvent: false });
		}

		const fs = this.flightSimulatorDetails;

		const ensure = (name: string) => {
			if (!fs.get(name)) {
				fs.addControl(name, new UntypedFormControl(""), { emitEvent: false });
			}
		};

		ensure("aircraftSimReg");
		ensure("acType");
		ensure("airportRoute");
		ensure("sectorNo");
		ensure("accumulatedSectors");

		fs.updateValueAndValidity({ emitEvent: false });
	}

	// ========== Datepicker helpers ==========
	updateDisplayDateFormat(date: Date) {
		const datePicker = this.datePickerRef?.nativeElement;
		if (datePicker && date !== null) {
			const formatted = moment(date).format(this.DATE_FORMAT.LICENSE_DATE_FORMAT);
			datePicker.value = formatted;
			this.cd.detectChanges();
		}
	}

	onDatePickerInit(datePickerRef?: ElementRef) {
		this.datePickerRef = datePickerRef;
	}

	handleDateChange(dateString: string): void {
		if (!isEmpty(dateString)) {
			this.saveLicenseExpiryDate(dateString, !this.isBootstrapping);
		}
	}

	// ========== Staff ID lookup ==========
	onFocusOut(_: any) {
		this.candidateForm.markAsTouched();
		this.isOnline = navigator.onLine;
		if (this.rejectOwnStaffNumber()) return;

		this.cvs.validateStaffId(this.staffNumber);

		if (this.staffNumber.valid && this.staffNumber.value?.length === 7) {
			if (!navigator.onLine) return;

			this.loaderService.setLoading(true);
			this.httpService.getByStaffNumber(this.staffNumber.value).subscribe({
				next: this.onSuccessStaffId.bind(this),
				error: this.onErrorStaffId.bind(this),
			});
		}
	}

	private rejectOwnStaffNumber(): boolean {
		const rejected = rejectInstructorOwnStaffNumber(this.candidateForm, this.profileService.profile?.staff_number);
		if (rejected) {
			this.loaderService.setLoading(false);
			this.cd.detectChanges();
		}
		return rejected;
	}

	onSuccessStaffId(resp: { data: StaffDetails }) {
		const staffDetail = resp.data;

		const rankOption =
			this.sharedService.getFilteredRankOptions(this.formType).find((v) => v.code === staffDetail.rank) ?? "";

		const licenseTypeOption =
			this.sharedService.getLicenseTypeOptions(this.formType)?.find((v) => v.code === staffDetail.licenseType) ?? "";

		// Patch basic fields silently
		this.name.patchValue(staffDetail.name, { emitEvent: false });

		if (isEmpty(this.rank.value)) {
			this.rank.patchValue(rankOption, { emitEvent: false });
		}
		if (isEmpty(this.licenseNo.value)) {
			this.licenseNo.patchValue(staffDetail.licenseNumber, { emitEvent: false });
		}
		if (isEmpty(this.licenseType.value)) {
			this.licenseType.setValue(licenseTypeOption, { emitEvent: true });
		}
		patchStaffLicenseExpiryDate(this.licenseExpiryDate, staffDetail.licenseExpiryDate);

		this.candidateForm.updateValueAndValidity({ emitEvent: false });

		this.datePickerInput.markAllAsTouched();
		this.candidateForm.markAsTouched();
		this.loaderService.setLoading(false);
		this.cd.detectChanges();
	}

	onErrorStaffId(resp: any) {
		if (resp?.includes?.("No profile found")) {
			this.name.patchValue("", { emitEvent: false });
			this.rank.patchValue("", { emitEvent: false });
			this.licenseNo.patchValue("", { emitEvent: false });
			this.staffNumber?.setErrors({ invalidStaffId: true });
		}
		this.loaderService.setLoading(false);
	}

	// ========== Session → Flight/Sim disablement logic ==========
	private attachFlightSimToggleWatcher() {
		const sd = this.sessionsDetails;
		if (!sd) return;
		sd.valueChanges.subscribe(() => {
			let disable = false;

			// For TS3 and TS5, check for TS3/TS5 specific exempt sessions
			if (this.isTS3 || this.isTS5) {
				disable = this.isAnyTS3TS5ExemptSessionSelected();
			} else {
				// For other forms (not TS1, TS3, TS5), check for general exempt sessions
				disable = !this.isTS1 && this.isAnyExemptSessionSelected();
			}

			this.applyFlightSimDisablement(disable);
		});
	}

	private isAnyExemptSessionSelected(): boolean {
		return this.getExemptHits().length > 0;
	}

	private isAnyTS3TS5ExemptSessionSelected(): boolean {
		const sd = this.sessionsDetails;
		if (!sd) return false;

		for (const g of this.sessionGroups) {
			const grp = sd.get(g.code) as UntypedFormGroup | null;
			if (!grp) continue;

			for (const item of g.items) {
				const ctrl = grp.get(item.code) as UntypedFormControl | null;
				if (!ctrl) continue;

				if (ctrl.value === true) {
					const normalized = (item.code ?? "").toString().trim().toUpperCase();
					// Check both the original format and normalized format (slash/underscore variants)
					if (
						this.TS3_TS5_EXEMPT_SESSIONS.has(normalized) ||
						this.TS3_TS5_EXEMPT_SESSIONS.has(normalized.replace(/\//g, "_")) ||
						this.TS3_TS5_EXEMPT_SESSIONS.has(normalized.replace(/_/g, "/"))
					) {
						return true;
					}
				}
			}
		}
		return false;
	}

	private applyFlightSimDisablement(disable: boolean) {
		this.flightSimDisabled = disable;

		const controls = [this.aircraftSimReg, this.acType, this.airportRoute, this.sectorNo, this.accumulatedSectors];

		if (disable) {
			// Clear values & reset state (looks brand new)
			this.aircraftSimReg.setValue("", { emitEvent: false });
			this.airportRoute.setValue("", { emitEvent: false });
			this.acType.setValue("", { emitEvent: false });
			this.sectorNo.setValue("", { emitEvent: false });
			this.accumulatedSectors.setValue("", { emitEvent: false });

			controls.forEach((c) => {
				// Clear any existing errors first
				c.setErrors(null);
				// Clear all validators - fields are NOT required when exempt sessions are selected
				c.clearValidators();
				// Disable the control (disabled controls are excluded from validation automatically)
				if (!c.disabled) c.disable({ emitEvent: false });
				// Mark as pristine and untouched
				c.markAsPristine();
				c.markAsUntouched();
				// Update validity (disabled controls are automatically valid)
				c.updateValueAndValidity({ emitEvent: false });
			});
		} else {
			// Re-enable fields (they don't have required validators by default)
			controls.forEach((c) => {
				if (c.disabled) c.enable({ emitEvent: false });
				// Don't add validators - flight/simulator fields are optional by default
				// Validators are only added if needed by specific form requirements
				c.updateValueAndValidity({ emitEvent: false });
			});
		}

		// Update parent form groups to ensure validation state propagates
		// Use emitEvent: true for the final update to trigger parent form validation
		// Use setTimeout to ensure all changes are applied before validation
		setTimeout(() => {
			this.flightSimulatorDetails.updateValueAndValidity({ emitEvent: true });
			this.candidateForm.updateValueAndValidity({ emitEvent: true });

			// Also update the parent form array if it exists (candidates array)
			// This ensures the training info form recognizes the candidate form as valid
			if (this.parentForm) {
				this.parentForm.updateValueAndValidity({ emitEvent: true });
			}

			this.cd.detectChanges();
		}, 0);
	}

	// ========== “Save” helpers used by template ==========
	private patchUserIntent<T = any>(ctrl: UntypedFormControl, value: T) {
		ctrl.patchValue(value, { emitEvent: !this.isBootstrapping });
	}

	saveName(value: string): void {
		this.patchUserIntent(this.name as any, value);
	}
	saveRank(value: any): void {
		this.patchUserIntent(this.rank as any, value);
	}
	saveLicenseType(value: any): void {
		this.patchUserIntent(this.licenseType as any, value);
	}
	saveStaffNumber(value: string): void {
		this.patchUserIntent(this.staffNumber as any, value);
	}
	savePilotProfile(value: any): void {
		this.patchUserIntent(this.pilotProfile as any, value);
	}
	saveLicenseNo(value: string): void {
		this.patchUserIntent(this.licenseNo as any, value);
	}
	saveLicenseExpiryDate(value: string, emit = true): void {
		const newDate = moment(value).format("YYYY-MM-DD");
		this.licenseExpiryDate.patchValue(newDate, { emitEvent: emit && !this.isBootstrapping });
	}

	// Flight/Sim “save” helpers (used by the autocomplete focusOut)
	saveACType(val: any) {
		this.acType.patchValue(val, { emitEvent: true });
	}
	saveSecNum(val: any) {
		this.sectorNo.patchValue(val, { emitEvent: true });
	}
	saveAccSec(val: any) {
		this.accumulatedSectors.patchValue(val, { emitEvent: true });
	}

	// ========== Payload helper ==========
	/**
	 * Collects selected session detail codes per group.
	 * Returns: { [groupCode]: string[] }
	 */
	getSelectedSessions(): Record<string, string[]> {
		const out: Record<string, string[]> = {};
		const sd = this.sessionsDetails;
		if (!sd) return out;

		for (const g of this.sessionGroups) {
			const grp = sd.get(g.code) as UntypedFormGroup | null;
			if (!grp) continue;

			const picked: string[] = [];
			for (const item of g.items) {
				const ctrl = grp.get(item.code) as UntypedFormControl | null;
				if (ctrl?.value === true) picked.push(item.code);
			}
			out[g.code] = picked;
		}
		return out;
	}

	// ========== Form getters used by the template ==========
	get name() {
		return this.candidateForm.get("name")!;
	}
	get rank() {
		return this.candidateForm.get("rank")!;
	}
	get staffNumber() {
		return this.candidateForm.get("staffNumber")!;
	}
	get pilotProfile() {
		return this.candidateForm.get("pilotProfile")!;
	}
	get licenseType() {
		return this.candidateForm.get("licenseType")!;
	}
	get licenseNo() {
		return this.candidateForm.get("licenseNo")!;
	}
	get licenseExpiryDate() {
		return this.candidateForm.get("licenseExpiryDate")! as UntypedFormControl;
	}

	// Sessions root FG
	get sessionsDetails(): UntypedFormGroup {
		return this.candidateForm.get("sessionDetails") as UntypedFormGroup;
	}

	// Flight/Sim root FG + inner controls (names must match your template)
	get flightSimulatorDetails() {
		return this.candidateForm.get("flightSimulatorDetails") as UntypedFormGroup;
	}
	get aircraftSimReg() {
		return this.candidateForm.get("flightSimulatorDetails.aircraftSimReg") as UntypedFormControl;
	}
	get acType() {
		return this.candidateForm.get("flightSimulatorDetails.acType") as UntypedFormControl;
	}
	get airportRoute() {
		return this.candidateForm.get("flightSimulatorDetails.airportRoute") as UntypedFormControl;
	}
	get sectorNo() {
		return this.candidateForm.get("flightSimulatorDetails.sectorNo") as UntypedFormControl;
	}
	get accumulatedSectors() {
		return this.candidateForm.get("flightSimulatorDetails.accumulatedSectors") as UntypedFormControl;
	}

	get filteredRankOptions() {
		return this.sharedService.getFilteredRankOptions(this.formType);
	}
}
